import compact from "lodash/compact";
import type { FunctionComponent } from "react";
import { useEffect, useTransition } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import type { allVideosContents } from "scmp-app/queries/__generated__/allVideosContents.graphql";
import type { videoListGridQuery$key } from "scmp-app/queries/__generated__/videoListGridQuery.graphql";

import { CardsContainer, Container, LoadMoreButton } from "./styles";
import { VideoListGridCard } from "./video-list-grid-card";

type Props = {
  className?: string;
  filterEntityUuid?: string;
  reference: videoListGridQuery$key;
};

const PAGE_SIZE = 12;

export const VideoListGrid: FunctionComponent<Props> = ({
  className,
  filterEntityUuid,
  reference,
}) => {
  const [isPending, startTransition] = useTransition();

  const {
    data: { contents },
    hasNext,
    loadNext,
    refetch,
  } = usePaginationFragment<allVideosContents, videoListGridQuery$key>(
    graphql`
      fragment videoListGridQuery on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int" }
        sectionIds: { type: "[String]" }
      )
      @refetchable(queryName: "allVideosContents") {
        contents(
          after: $after
          first: $count
          filter: { sectionIds: $sectionIds }
          contentTypes: [VIDEO]
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "allVideos_contents") {
          edges {
            node {
              ... on Video {
                entityId
                ...videoListGridCardContent
              }
            }
          }
        }
      }
    `,
    reference,
  );

  useEffect(() => {
    if (filterEntityUuid === undefined) return;
    startTransition(() => {
      refetch({
        count: PAGE_SIZE * 2,
        sectionIds: compact([filterEntityUuid]),
      });
    });
  }, [filterEntityUuid, refetch, startTransition]);

  return (
    <Container $isPending={isPending} className={className}>
      <CardsContainer>
        {contents.edges.map(({ node }) => (
          <VideoListGridCard key={node.entityId} reference={node} />
        ))}
      </CardsContainer>

      {hasNext && <LoadMoreButton onClick={() => loadNext(PAGE_SIZE)}>Watch More</LoadMoreButton>}
    </Container>
  );
};
